import type { FileOrFolder } from '../../types/FileOrFolder';
import type { SearchPageMetadata, SearchPageRequest } from '../../types/SearchResultsPage';

// Keep browser runtime code inside the Next.js package. Shared interfaces remain
// in the repository-level types folder and are erased during compilation.
export function searchPageQuery({ query, pagination }: SearchPageRequest, exportAll = false) {
  const params = new URLSearchParams(query);
  params.set('resultTab', pagination.tab);
  params.set('resultPage', String(pagination.page));
  params.set('resultPageSize', String(pagination.pageSize));
  params.set('resultSort', pagination.sort);
  params.set('resultOrder', pagination.order);
  params.set('resultView', pagination.view);
  params.set('resultDocs', pagination.documents);
  if (exportAll) params.set('resultExport', 'true');
  else params.delete('resultExport');
  return params.toString();
}

// Tab, page, and sort changes can reuse one eager result set. View/document
// filters remain in the key because they change which records the server sends.
export function eagerSearchKey({ query, pagination }: SearchPageRequest) {
  const params = new URLSearchParams(query);
  for (const key of [...params.keys()]) {
    if (key.startsWith('result')) params.delete(key);
  }
  params.sort();
  return JSON.stringify([params.toString(), pagination.view, pagination.documents]);
}

function resultComparator(request: SearchPageRequest) {
  const { pagination } = request;
  const time = (value: unknown) => {
    const number = value ? new Date(value as string).getTime() : 0;
    return Number.isFinite(number) ? number : 0;
  };
  const number = (value: unknown) => Number(String(value ?? '').replace(/\D/g, '')) || 0;
  return (a: FileOrFolder, b: FileOrFolder) => {
    const aDate = pagination.sort === 'meetingDate' && pagination.tab !== 'documents'
      ? a.bcrContent?.dulyConvenedMeeting ?? a.meetingMinuteContent?.meetingDate : a.createdAt;
    const bDate = pagination.sort === 'meetingDate' && pagination.tab !== 'documents'
      ? b.bcrContent?.dulyConvenedMeeting ?? b.meetingMinuteContent?.meetingDate : b.createdAt;
    const diff = time(aDate) - time(bDate)
      || number(a.bcrContent?.resolutionNumber ?? a.meetingMinuteContent?.uniqueId)
        - number(b.bcrContent?.resolutionNumber ?? b.meetingMinuteContent?.uniqueId)
      || String(a._id).localeCompare(String(b._id));
    return pagination.order === 'asc' ? diff : -diff;
  };
}

export function selectEagerSearchPage(items: FileOrFolder[], request: SearchPageRequest, source: SearchPageMetadata) {
  const { pagination } = request;
  const uniqueItems = [...new Map(items.map(item => [String(item._id), item])).values()];
  const bcrs = uniqueItems.filter(item => pagination.view !== 'meeting-minutes'
    && item.type === 'bcr-pdf' && item.bcrContent && item.bcrContent.isDraft !== true);
  const minutes = uniqueItems.filter(item => pagination.view !== 'bcrs'
    && item.meetingMinuteContent && item.meetingMinuteContent.isDraft !== true);
  const documentView = pagination.view === 'all' ? pagination.documents : pagination.view;
  const documents = uniqueItems.filter(item =>
    (documentView !== 'meeting-minutes' && !!item.attachDocBCR)
    || (documentView !== 'bcrs' && !!item.attachDocMeetingMinute));
  const selected = pagination.tab === 'bcrs' ? bcrs : pagination.tab === 'meeting-minutes' ? minutes : documents;
  const total = pagination.tab === 'bcrs' ? source.totals.bcrs
    : pagination.tab === 'meeting-minutes' ? source.totals.meetingMinutes : source.totals.documents;
  const page = Math.min(pagination.page, Math.max(1, Math.ceil(total / pagination.pageSize)));
  const start = (page - 1) * pagination.pageSize;
  return {
    items: selected.sort(resultComparator(request)).slice(start, start + pagination.pageSize),
    pagination: { ...source, tab: pagination.tab, page, pageSize: pagination.pageSize },
  };
}
